import type { OrcaCortexFailureCode } from "../../types/orcaGuidance"

export interface ConnectedDirectiveState {
  text: string
  fullText?: string
  actions?: { label: string; type?: string }[]
  isTyping?: boolean
  readingStatus?: "ready" | "loading" | "fallback" | "error"
  failureCode?: OrcaCortexFailureCode
}

interface ConnectedDirectiveGuidanceProps {
  directive: ConnectedDirectiveState
  presentation: "inline" | "sheet"
  isUpdating?: boolean
  brandIconSrc?: string
  showLabel?: boolean
}

/**
 * One live directive presentation shared by the inline desktop composer and
 * the mobile sheet adapter. Layout chrome may change; directive state and
 * announcement behavior do not.
 */
export function ConnectedDirectiveGuidance({
  directive,
  presentation,
  isUpdating = false,
  brandIconSrc,
  showLabel = false,
}: ConnectedDirectiveGuidanceProps) {
  const readingStatus = directive.readingStatus ?? "ready"
  const readingLoading = readingStatus === "loading"
  const hasVisibleText = Boolean(directive.text.replace(/\u200B/g, "").trim())
  const visibleText = hasVisibleText
    ? directive.text
    : readingStatus === "loading"
      ? "Orca is reading the latest verified project context."
      : readingStatus === "error"
        ? "Orca couldn’t complete the live project reading. The primary reading remains available."
        : readingStatus === "fallback"
          ? "Current project guidance remains available while Orca prepares a separate live reading."
          : directive.text
  const readingNotice = readingStatus === "error" && hasVisibleText
    ? "The live reading could not refresh. Showing the last available reading."
    : readingStatus === "fallback" && hasVisibleText
      ? "Using compatibility guidance while Orca prepares a separate live reading."
      : null
  const reserveFinalHeight = Boolean(directive.isTyping && directive.fullText)
  const textClassName = presentation === "sheet"
    ? "bearing-home-composer-directive-text"
    : `orca-directive-text${isUpdating || readingLoading ? " is-swapping" : ""}`

  return (
    <div
      className={`connected-directive-guidance connected-directive-guidance--${presentation}${presentation === "sheet" ? " bearing-home-composer-directive-copy" : ""}`}
      data-connected-directive-composer-part="guidance"
      data-presentation={presentation}
      data-reading-status={readingStatus}
      aria-busy={isUpdating || readingLoading || undefined}
    >
      {presentation === "sheet" && brandIconSrc && (
        <img
          className="bearing-home-composer-directive-icon"
          src={brandIconSrc}
          alt=""
          aria-hidden="true"
          draggable={false}
        />
      )}

      {presentation === "inline" && showLabel && (
        <p className="orca-directive-label">ORCA Directive</p>
      )}

      <div
        className={textClassName}
        style={{ position: "relative" }}
        aria-live={readingLoading ? "off" : "polite"}
      >
        {reserveFinalHeight && (
          <div aria-hidden style={{ visibility: "hidden", pointerEvents: "none" }}>
            {directive.fullText}
          </div>
        )}
        <div
          style={reserveFinalHeight
            ? { position: "absolute", top: 0, left: 0, right: 0 }
            : undefined}
        >
          {visibleText}
          {directive.isTyping && <span className="orca-directive-cursor" />}
        </div>
      </div>

      {readingNotice && (
        <div
          className="orca-directive-reading-note"
          role="status"
          aria-live="polite"
        >
          {readingNotice}
        </div>
      )}

      {(isUpdating || readingLoading) && (
        <div
          className={`orca-directive-loading show${presentation === "sheet" ? " bearing-home-composer-directive-loading" : ""}`}
          role="status"
          aria-live="polite"
        >
          <div className="orca-directive-loading-head">
            <span className="orca-directive-loading-dot" aria-hidden="true" />
            {readingLoading
              ? "Orca is reading the latest project context"
              : "Orca is updating your directive"}
          </div>
          <span className="orca-directive-loading-line is-long" aria-hidden="true" />
          <span className="orca-directive-loading-line is-short" aria-hidden="true" />
        </div>
      )}
    </div>
  )
}

export default ConnectedDirectiveGuidance
